import React, { useEffect, useState } from 'react'
import { Reports } from '../../core/libraryModels'
import { KTSVG } from '../../../../../_metronic/helpers'
import type { LibraryApi, LibraryTemplateFilters } from '../../core/libraryApi'

interface Props {
  isOpen: boolean
  onClose: () => void
  onReportAdded: () => void
  api: LibraryApi
  templateContext?: LibraryTemplateFilters
}

export type { Props as EditReportModalProps }

const EditReportModal: React.FC<Props> = ({ onClose, isOpen, onReportAdded, api, templateContext }) => {
  const [reports, setReports] = useState<Reports[]>([])
  const [editingReportId, setEditingReportId] = useState<number | null>(null)
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [deletingReportId, setDeletingReportId] = useState<number | null>(null)
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null)

  useEffect(() => {
    fetchReportsList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchReportsList = async () => {
    try {
      const templateIDs = await fetchTemplateIds()

      if (templateIDs && templateIDs.length > 0) {
        const reportsPerTemplate = await Promise.all(templateIDs.map((templateId) => api.getReportsForTemplate(templateId)))
        setReports(reportsPerTemplate.flat())
      }
    } catch (err) {
      console.error('Failed to fetch reports list:', err)
      return []
    }
  }

  const fetchTemplateIds = async () => {
    try {
      const templateList = await api.getTemplateList(templateContext)
      return templateList.map((template) => template.id)
    } catch (err) {
      console.error('Failed to fetch template list:', err)
    }
  }

  const handleUpdateClick = async (tempId: number, reportId: number) => {
    if (!editingReportId) return

    if (!editingName.trim()) {
      alert('Please enter a report name')
      return
    }

    try {
      const response = await api.updateMenuItem(tempId, reportId, editingName, true)
      showSuccessAndReset('Report name updated successfully')
      onReportAdded()
    } catch (err) {
      console.error('Failed to update report:', err)
    }
  }

  const handleDeleteClick = async (tempId: number) => {
    if (!deletingReportId) return

    try {
      const response = await api.deleteTemplate(tempId)
      showSuccessAndReset('Report deleted successfully')
      onReportAdded()
    } catch (err) {
      console.error('Failed to delete report:', err)
    }
  }

  const showSuccessAndReset = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => {
      setSuccessMessage('')
      setEditingReportId(null)
      setEditingTemplateId(null)
      setDeletingReportId(null)
      setDeletingTemplateId(null)
      fetchReportsList()
    }, 2000)
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Edit Report</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body'>
          {editingReportId === null && deletingReportId === null && successMessage === '' && (
            <ul>
              {reports.map((report, index) => (
                <li key={index} className='list-group-item d-flex align-items-center justify-content-between'>
                  <div
                    className='mt-3 p-4 border rounded-1 flex-grow-1 me-3'
                    style={{ backgroundColor: '#F9FAFB', borderColor: '#D1D5DB', cursor: 'pointer' }}
                  >
                    {report.name}
                  </div>
                  <button
                    className='btn btn-sm px-2'
                    onClick={() => {
                      setEditingReportId(report.id)
                      setEditingTemplateId(report.templateId)
                      setEditingName(report.name)
                      setSuccessMessage('')
                    }}
                  >
                    <KTSVG path='/media/map/edit-active.svg' className='' />
                  </button>
                  <button
                    className='btn btn-sm px-2'
                    onClick={() => {
                      setDeletingReportId(report.id)
                      setDeletingTemplateId(report.templateId)
                      setSuccessMessage('')
                    }}
                  >
                    <KTSVG path='/media/map/trash.svg' className='' />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {editingReportId !== null && editingTemplateId !== null && successMessage === '' && (
            <div>
              <label className='modal_label'>Report Name</label>
              <input
                type='text'
                className='form-control mb-3'
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
              />
              <div className='d-flex gap-2 mt-5'>
                <button className='btn btn_success' onClick={() => handleUpdateClick(editingTemplateId, editingReportId)}>
                  Update
                </button>
                <button
                  className='btn btn_secondary'
                  onClick={() => {
                    setEditingReportId(null)
                    setEditingTemplateId(null)
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {deletingReportId !== null && deletingTemplateId !== null && successMessage === '' && (
            <div className='mt-3'>
              <div className='d-flex justify-content-center mb-4'>
                <KTSVG path='/media/map/exclamation.svg' className='svg-icon-4x'></KTSVG>
              </div>
              <div className='text-center'>Are you sure you want to delete this Report?</div>
              <div className='d-flex justify-content-center gap-2 mt-5'>
                <button
                  className='btn btn_secondary'
                  onClick={() => {
                    setDeletingReportId(null)
                    setDeletingTemplateId(null)
                  }}
                >
                  No, Keep it
                </button>
                <button className='btn btn_danger me-2' onClick={() => handleDeleteClick(deletingTemplateId)}>
                  Yes, Delete!
                </button>
              </div>
            </div>
          )}

          {successMessage && (
            <div className='text-center'>
              <div className='d-flex justify-content-center mb-4'>
                <KTSVG path='/media/map/verify.svg' className='svg-icon-4x'></KTSVG>
              </div>
              <p className='text-success'>{successMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditReportModal
